import React, { useCallback, useEffect, useRef, useState } from 'react';

// A two-pane horizontal split with a draggable divider carrying a centered grip button. Dragging the
// grip left/right resizes the panes (the left pane's width %, clamped to [minPct, maxPct]); content
// reflows/scrolls within each pane. Used for passage (left) ↔ questions (right) reading layouts.
const ResizableSplit = ({ left, right, initialLeftPct = 50, minPct = 25, maxPct = 75, gap = '1rem', style }) => {
    const containerRef = useRef(null);
    const [leftPct, setLeftPct] = useState(initialLeftPct);
    const draggingRef = useRef(false);

    const onMove = useCallback((clientX) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0) return;
        const pct = ((clientX - rect.left) / rect.width) * 100;
        setLeftPct(Math.max(minPct, Math.min(maxPct, pct)));
    }, [minPct, maxPct]);

    useEffect(() => {
        const handleMouseMove = (e) => { if (draggingRef.current) { e.preventDefault(); onMove(e.clientX); } };
        const handleTouchMove = (e) => { if (draggingRef.current && e.touches[0]) onMove(e.touches[0].clientX); };
        const stop = () => {
            draggingRef.current = false;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stop);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', stop);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stop);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', stop);
        };
    }, [onMove]);

    const startDrag = (e) => {
        draggingRef.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        if (e.type === 'touchstart' && e.touches[0]) onMove(e.touches[0].clientX);
    };

    return (
        <div ref={containerRef} style={{ display: 'flex', alignItems: 'stretch', minHeight: 0, ...(style || {}) }}>
            <div style={{ flex: `0 0 ${leftPct}%`, minWidth: 0, overflowY: 'auto' }}>{left}</div>
            {/* Divider with a centered grip the reader drags to resize the panes. */}
            <div
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                title="Drag to resize"
                style={{
                    flex: `0 0 ${gap}`, alignSelf: 'stretch', cursor: 'col-resize', position: 'relative',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                <span style={{ position: 'absolute', top: 0, bottom: 0, width: '1px', background: '#d1d5db' }} />
                <span style={{
                    position: 'relative', zIndex: 1, width: '18px', height: '46px', borderRadius: '6px',
                    background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700,
                    boxShadow: '0 2px 6px -1px rgba(76,29,149,0.5)', letterSpacing: '-1px',
                }}>⋮⋮</span>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, overflowY: 'auto' }}>{right}</div>
        </div>
    );
};

export default ResizableSplit;
